import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  QueryList,
  Renderer2,
  signal,
  ViewChild,
  ViewChildren,
  WritableSignal,
} from '@angular/core';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { TranslateModule } from '@ngx-translate/core';
import { pathParameter, PushTagOnClickDirective, TagEvent } from '@libs/shared-services';
import { DateTime } from 'luxon';
import {
  distinctUntilKeyChanged,
  filter,
  fromEvent,
  interval,
  map,
  merge,
  pairwise,
  startWith,
  switchMap,
  take,
  takeUntil,
  takeWhile,
} from 'rxjs';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { Dialog } from '@angular/cdk/dialog';
import { DEFAULT_TEETH, PerioHistory, ToothWithLayers } from '@libs/ui-lib';
import { FindToothByDateAndPositionPipe, FindToothByPositionPipe } from '@libs/charting';
import { ActivatedRoute } from '@angular/router';
import { ToothComponent } from '../../tooth';
import { ToothDetailsDialogComponent } from '../tooth-details-dialog.component';
import { ToothDetailsView } from '../tooth-details';
import { PerioToothComponent } from '../../perio-tooth';

const HISTORY_ELEMENT_WIDTH = 70;
const ANIMATION_CALCULATION_INTERVAL = 25;

interface ElementDimensionsToCalculateHorizontalDistance {
  x: number;
  width: number;
}

@Component({
  selector: 'ffd-tooth-highlight',
  templateUrl: 'tooth-highlight.component.html',
  styleUrls: ['./tooth-highlight.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ToothComponent,
    SvgIconComponent,
    TranslateModule,
    PushTagOnClickDirective,
    LottieComponent,
    PerioToothComponent,
    FindToothByPositionPipe,
    FindToothByDateAndPositionPipe,
  ],
})
export class ToothHighlightComponent implements AfterViewInit {
  private readonly renderer = inject(Renderer2);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(Dialog);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly toothDetailsDialog = inject(ToothDetailsDialogComponent);

  public readonly lottieOptions: AnimationOptions = {
    path: '/assets/animations/loading-spinner.json',
  };

  // first in array is the latest
  @Input({ required: true }) public set toothHistory(value: ToothWithLayers[]) {
    this.toothHistorySignal.set(value);
  }
  @Input() public set perioHistory(value: PerioHistory[]) {
    this.perioHistorySignal.set(value);
  }
  @Input() public patientId!: string;
  @Input({ required: true }) public set defaultView(value: ToothDetailsView) {
    this.setActiveTabId(value);
  }
  @Input() public set date(value: number | undefined) {
    if (value) {
      this.selectedDate.set(value);
    }
  }
  @Output() public leftClick = new EventEmitter<void>();
  @Output() public rightClick = new EventEmitter<void>();
  @Output() public historyClick = new EventEmitter<string>();
  @Output() public toggleClick = new EventEmitter<ToothDetailsView>();

  @ViewChild('toothHistory') protected toothHistoryElement!: ElementRef<HTMLElement>;
  @ViewChild('toothHistoryWrapper') protected toothHistoryWrapperElement!: ElementRef<HTMLElement>;
  @ViewChildren('historyItemRef') protected toothHistoryItemRefList!: QueryList<ElementRef<HTMLElement>>;

  protected activatedRouteParamsSignal = toSignal(this.activatedRoute.params.pipe(filter((param) => !!param)));
  protected toothHistorySignal = signal<ToothWithLayers[] | undefined>([]);
  protected perioHistorySignal: WritableSignal<PerioHistory[]> = signal([]);
  protected selectedIndex = signal(0);
  protected selectedIndex$ = toObservable(this.selectedIndex);
  protected selectedDate = signal(0);
  // eslint-disable-next-line unicorn/no-useless-undefined
  protected currentToothPosition = computed(() => Number.parseInt(this.activatedRouteParamsSignal()![pathParameter('toothPosition')]));
  protected currentPosition = signal(0);
  protected dragging = signal(false);
  protected dragging$ = toObservable(this.dragging);
  protected blockHistoryClick = signal(false);
  protected TagEvent = TagEvent;

  protected toothHistoryCenterDimensions?: ElementDimensionsToCalculateHorizontalDistance;

  public DEFAULT_TEETH = DEFAULT_TEETH;

  public readonly tabs: { label: string; id: ToothDetailsView }[] = [
    { label: 'charting_details_toggle_charting', id: 'charting' },
    { label: 'charting_details_toggle_perio', id: 'perio' },
  ];
  public readonly activeTabId = signal(this.tabs[0].id);

  public isToothMissing = computed(() => {
    const toothHistory = this.toothHistorySignal();

    if (!toothHistory || toothHistory.length === 0) {
      return false;
    }

    return toothHistory[this.selectedIndex()].currentState?.layers.length === 0;
  });

  public currentTooth = computed(() => {
    const toothHistory = this.toothHistorySignal();

    if (!toothHistory) {
      return;
    }

    const pos = this.currentToothPosition();
    return toothHistory.find((t) => t.position === pos);
  });

  public ngAfterViewInit(): void {
    this.listenToToothHistoryDrag();
    this.setHistoryItemScaleAnimations();

    if (this.activeTabId() === 'perio') {
      this.scrollToSelectedPerioDate();
    }
  }

  public setActiveTabId(id: ToothDetailsView): void {
    this.activeTabId.set(id);
    this.resetSelectedTabIndex();
    if (this.toothHistoryElement?.nativeElement) {
      this.renderer.setStyle(this.toothHistoryElement.nativeElement, 'translate', '0');
    }
    this.setHistoryItemScaleAnimations();
    this.toggleClick.emit(id);
  }

  public onLeftClick(): void {
    this.resetSelectedTabIndex();
    this.renderer.setStyle(this.toothHistoryElement?.nativeElement, 'translate', '0');
    this.setHistoryItemScaleAnimations();
    this.leftClick.emit();
  }

  public onRightClick(): void {
    this.resetSelectedTabIndex();
    this.renderer.setStyle(this.toothHistoryElement?.nativeElement, 'translate', '0');
    this.setHistoryItemScaleAnimations();
    this.rightClick.emit();
  }

  public onHistoryItemClick(date: number, selectedIndex: number): void {
    if (this.blockHistoryClick()) {
      this.blockHistoryClick.set(false);
      return;
    }
    const visitDate = DateTime.fromMillis(date).toUTC().toFormat('yyyy-MM-dd');
    this.historyClick.emit(visitDate);
    this.selectedDate.set(date);
    this.selectedIndex.set(selectedIndex);
    this.currentPosition.set(selectedIndex * HISTORY_ELEMENT_WIDTH);
    this.setHistoryItemScaleAnimations();
  }

  /* This one deserves a place in a utility module rather than being here as a method */
  private distanceToScale(distance: number, minDistance = 0, maxDistance = 600, maxScale = 1, minScale = 0.5, exponentCurve = 1): number {
    if (distance <= minDistance) return maxScale;
    if (distance >= maxDistance) return minScale;

    const t = Math.pow((distance - minDistance) / (maxDistance - minDistance), exponentCurve); // 0 → 1
    return maxScale + (minScale - maxScale) * t; // lerp-inverse
  }

  private getElementDimensions(el: HTMLElement): ElementDimensionsToCalculateHorizontalDistance {
    const { left, width } = el.getBoundingClientRect();

    return { width, x: left + width / 2 };
  }

  private getDistanceXFromPoint(pointX: number, el: HTMLElement): number {
    return Math.abs(pointX - this.getElementDimensions(el).x);
  }

  private resetSelectedTabIndex(): void {
    this.selectedIndex.set(0);
    this.selectedDate.set(0);
  }

  private scrollToSelectedPerioDate(): void {
    const selectedDate = this.selectedDate();
    const selectedIndex = this.perioHistorySignal()?.findIndex((item) => item.date === selectedDate);
    if (selectedIndex && selectedIndex !== -1) {
      this.onHistoryItemClick(selectedDate, selectedIndex);
    }
  }

  private setHistoryItemScaleAnimations(): void {
    this.toothHistoryItemRefList?.changes
      .pipe(
        take(1),
        switchMap(() => this.selectedIndex$),
        takeUntilDestroyed(this.destroyRef),
        startWith(this.selectedIndex()),
      )
      .subscribe(() => {
        this.toothHistoryCenterDimensions = this.getElementDimensions(this.toothHistoryWrapperElement.nativeElement);
        this.toothHistoryItemRefList?.forEach((ref, index) => {
          // eslint-disable-next-line @typescript-eslint/no-magic-numbers
          this.renderer.setStyle(ref.nativeElement, 'scale', 1 - Math.abs(this.selectedIndex() - index) * 0.25);
        });

        // Apply transition classes whenever the QueryList changes or scale is updated
        setTimeout(() => {
          this.toothHistoryItemRefList?.forEach((ref) => {
            this.renderer.addClass(ref.nativeElement, 'transition-all');
            this.renderer.addClass(ref.nativeElement, 'duration-500');
          });
          // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        }, 10);
      });

    this.dragging$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(Boolean),
        switchMap(() => interval(ANIMATION_CALCULATION_INTERVAL).pipe(takeWhile(this.dragging))),
      )
      .subscribe(() => {
        this.toothHistoryItemRefList.forEach((ref) => {
          const el = ref.nativeElement;
          const distance = this.getDistanceXFromPoint(this.toothHistoryCenterDimensions!.x, el);

          this.renderer.setStyle(el, 'scale', this.distanceToScale(distance, 0, this.toothHistoryCenterDimensions!.width * 2));
        });
      });
  }

  private listenToToothHistoryDrag(): void {
    // We check if the emission since the drag has started is the first or not
    let firstEmission = true;
    fromEvent(this.toothHistoryElement.nativeElement, 'mouseleave')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.blockHistoryClick.set(false);
      });

    fromEvent<DragEvent>(this.toothHistoryElement.nativeElement, 'mousedown')
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((startDragEvent) => {
          startDragEvent.preventDefault();
          this.dragging.set(true);
          return fromEvent<DragEvent>(document, 'mousemove').pipe(
            distinctUntilKeyChanged('clientX'),
            takeUntil(
              merge(fromEvent<MouseEvent>(document, 'mouseup'), fromEvent(document, 'mouseleave')).pipe(
                map((event) => {
                  if (event.type === 'mouseleave') {
                    this.blockHistoryClick.set(false);
                  }
                  firstEmission = true;
                  this.dragging.set(false);
                }),
              ),
            ),
            pairwise(),
          );
        }),
      )
      .subscribe(([moveEventA, moveEventB]) => {
        const toothHistory = this.toothHistorySignal();
        if (firstEmission) {
          firstEmission = false;
          this.blockHistoryClick.set(true);
        }
        const currentPosition = this.currentPosition();
        const step = moveEventA.clientX - moveEventB.clientX;
        const value = currentPosition + step;
        const min = -((toothHistory?.length ?? 0) * HISTORY_ELEMENT_WIDTH) + HISTORY_ELEMENT_WIDTH;
        this.currentPosition.set(value);
        if (-value < 0 && -value > min) {
          this.renderer.setStyle(this.toothHistoryElement.nativeElement, 'translate', `-${value}px`);
        }
      });
  }
}
